// One X account in the all-accounts list (Settings → X accounts): identity, where it
// runs, today's activity, its automation switch, Switch to / Disconnect.

import type { IgAccount, IgAccountOverview } from "@gmx/shared";
import { Avatar, Badge, Button, Card, Switch, Tooltip, useConfirm, useToast } from "@gmx/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeftRight, ArrowUpRight, MonitorX, Unlink } from "lucide-react";
import { AccountTypeBadge } from "../AccountTypeBadge";
import { igAccountsApi, isApiError, settingsApi } from "../../lib/api";
import { activeDriver, runsInLine } from "../../lib/accountStatus";
import { formatCount, formatDate, formatNumber } from "../../lib/format";
import { IG_ACCOUNTS_KEY, IG_ACCOUNTS_OVERVIEW_KEY } from "../../lib/igAccount";
import { settingsKeys } from "../../lib/workspace";
import { RunStatusBadge } from "./IgRunStatus";
import "./ig-workspaces.css";

export interface IgAccountCardProps {
  account: IgAccount;
  /** Today's numbers etc. null while loading or when the API doesn't have the overview yet. */
  overview: IgAccountOverview | null;
  isCurrent: boolean;
  onSwitch: () => void;
}

export function IgAccountCard({ account, overview, isCurrent, onSwitch }: IgAccountCardProps) {
  const confirm = useConfirm();
  const toast = useToast();
  const qc = useQueryClient();
  const driver = activeDriver(account);

  const automation = useMutation({
    mutationFn: (enabled: boolean) => settingsApi.put<{ enabled: boolean }>("automation", { enabled }, account.id),
    onSuccess: (_res, enabled) => {
      void qc.invalidateQueries({ queryKey: IG_ACCOUNTS_OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: settingsKeys.scope(account.id) });
      toast.success(enabled ? `Automation is on for @${account.username}` : `Automation is off for @${account.username}`);
    },
    onError: (err) => toast.error(err),
  });

  const release = useMutation({
    mutationFn: () => igAccountsApi.releaseDriver(account.id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: IG_ACCOUNTS_KEY });
      toast.success(`@${account.username} is free`, "The next browser logged into it takes over.");
    },
    onError: (err) =>
      isApiError(err, 404) || isApiError(err, 405)
        ? toast.info("Not available yet", "Use \"Stop using here\" in that browser's extension instead.")
        : toast.error(err),
  });

  async function disconnect() {
    const ok = await confirm({
      title: `Disconnect @${account.username}?`,
      description: `This deletes @${account.username}'s contacts, campaigns and history from Grow My X: its pipeline, outreach and engage campaigns, scheduled posts, DM and comment history, and its settings. Your actual X account isn't touched.`,
      confirmLabel: "Disconnect and delete data",
      tone: "danger",
      requireText: account.username,
      action: () => igAccountsApi.disconnect(account.id),
    });
    if (!ok) return;
    qc.setQueryData<IgAccount[]>(IG_ACCOUNTS_KEY, (old) => old?.filter((a) => a.id !== account.id));
    void qc.invalidateQueries({ queryKey: IG_ACCOUNTS_KEY });
    toast.success(`@${account.username} disconnected`);
  }

  const today = overview?.today;
  const automationOn = overview?.automationEnabled;

  return (
    <li>
      <Card className={isCurrent ? "gfp-ws-card gfp-ws-card--current" : "gfp-ws-card"} padding="md">
        <div className="gfp-ws-card__top">
          <Avatar src={account.profilePicUrl} name={account.fullName || account.username} size={52} ring />
          <div className="gfp-ws-card__id">
            <div className="gfp-ws-card__line">
              <a className="gfp-ws-card__handle" href={`https://x.com/${encodeURIComponent(account.username)}/`} target="_blank" rel="noreferrer">
                @{account.username}
                <ArrowUpRight size={14} aria-hidden="true" />
                <span className="gf-sr-only">(opens X)</span>
              </a>
              <AccountTypeBadge type={account.accountType} />
              {isCurrent ? (
                <Badge size="sm" tone="accent">
                  Selected
                </Badge>
              ) : null}
            </div>
            <p className="gfp-ws-card__meta">
              {account.fullName ? `${account.fullName} · ` : ""}
              {formatCount(account.followers)} followers · {formatCount(account.following)} following · {formatCount(account.posts)} posts
            </p>
          </div>
          <div className="gfp-ws-card__actions">
            {isCurrent ? null : (
              <Button size="sm" variant="secondary" icon={<ArrowLeftRight size={15} />} onClick={onSwitch}>
                Switch to
              </Button>
            )}
            <Button variant="danger" size="sm" icon={<Unlink size={15} />} onClick={() => void disconnect()}>
              Disconnect
            </Button>
          </div>
        </div>

        <div className="gfp-ws-card__run">
          <RunStatusBadge account={account} />
          <span className="gfp-ws-card__runsin">{runsInLine(account)}</span>
          {driver ? (
            <Tooltip content="Lets another browser logged into this account take over right away, instead of after 15 quiet minutes.">
              <Button size="sm" variant="ghost" icon={<MonitorX size={14} />} loading={release.isPending} onClick={() => release.mutate()}>
                Stop running in that browser
              </Button>
            </Tooltip>
          ) : null}
        </div>

        <dl className="gfp-ws-card__stats">
          <div>
            <dt>Actions today</dt>
            <dd>{today ? formatNumber(today.actions) : "—"}</dd>
          </div>
          <div>
            <dt>DMs</dt>
            <dd>{today ? formatNumber(today.dmsSent) : "—"}</dd>
          </div>
          <div>
            <dt>Follows</dt>
            <dd>{today ? formatNumber(today.follows) : "—"}</dd>
          </div>
          <div>
            <dt>Replies</dt>
            <dd>{today ? formatNumber(today.replies) : "—"}</dd>
          </div>
          <div>
            <dt>Contacts</dt>
            <dd>{overview ? formatNumber(overview.contacts) : "—"}</dd>
          </div>
          <div className="gfp-ws-card__auto">
            <dt>Automation</dt>
            <dd>
              {automationOn === undefined ? (
                "—"
              ) : (
                <Switch
                  size="sm"
                  checked={automation.isPending ? !!automation.variables : automationOn}
                  disabled={automation.isPending}
                  onChange={(v) => automation.mutate(v)}
                  label={(automation.isPending ? !!automation.variables : automationOn) ? "On" : "Off"}
                  aria-label={`Automation for @${account.username}`}
                />
              )}
            </dd>
          </div>
        </dl>

        <p className="gfp-ws-card__foot">
          Connected {formatDate(account.createdAt)}. Settings, voice, campaigns and contacts are separate for every account.
        </p>
      </Card>
    </li>
  );
}
